import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, MapPin } from "lucide-react"

interface MatchIntroductionProps {
  match: {
    name: string
    age: number
    location: string
    occupation: string
    compatibility: string
    summary: string
    previouslyMarried?: boolean
    hasChildren?: boolean
  }
  onAccept: () => void
  onDecline: () => void
  onBack: () => void
}

export function MatchIntroduction({ match, onAccept, onDecline, onBack }: MatchIntroductionProps) {
  const [showWhite, setShowWhite] = useState(true)
  const [showBackground, setShowBackground] = useState(false)
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    const whiteTimer = setTimeout(() => setShowWhite(false), 800)
    const bgTimer = setTimeout(() => setShowBackground(true), 1200)
    const contentTimer = setTimeout(() => setShowContent(true), 3800)
    return () => {
      clearTimeout(whiteTimer)
      clearTimeout(bgTimer)
      clearTimeout(contentTimer)
    }
  }, [])

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      <AnimatePresence>
        {showWhite && (
          <motion.div
            className="absolute inset-0 bg-white z-50"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBackground && (
          <>
            <motion.div
              className="fixed inset-0 gradient-background"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 3.5, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div
              className="fixed inset-0 grain-texture"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 3.5, ease: [0.22, 1, 0.36, 1] }}
            />
          </>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: showContent ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        onClick={onBack}
        className="fixed top-4 left-4 md:top-8 md:left-8 text-white/80 hover:text-white text-sm font-light flex items-center gap-1 transition-colors z-20"
        type="button"
      >
        ← Back
      </motion.button>

      {showContent && (
        <motion.div
          className="relative z-10 flex flex-col items-center text-center px-4 md:px-6 py-16 md:py-20 w-full max-w-lg min-h-screen"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mb-4"
          >
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/10 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-3xl md:text-4xl font-light text-white shadow-[0_0_40px_rgba(255,255,255,0.2)]">
              {match.name.charAt(0)}
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-3xl md:text-4xl font-light text-white mb-2"
          >
            {match.name}, {match.age}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="text-white/70 font-light text-sm flex items-center gap-1.5 mb-4"
          >
            <MapPin className="w-3.5 h-3.5 text-white/50" />
            {match.location}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-wrap items-center justify-center gap-2 mb-6"
          >
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs text-white/80 font-light">
              {match.previouslyMarried ? "Previously married" : "Never married"}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs text-white/80 font-light">
              {match.hasChildren ? "Has children" : "No children"}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/30">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-sm text-white font-medium tracking-wide">
                {match.compatibility} Match
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 w-full"
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 md:p-5">
              <p className="text-[10px] text-white/50 font-medium uppercase tracking-[0.15em] mb-2">
                {match.name}&apos;s Intention
              </p>
              <p className="text-[11px] text-white/60 font-light italic mb-3">
                &quot;One thing that&apos;s important for you to understand about me...&quot;
              </p>
              <p className="text-sm text-white/90 font-light leading-relaxed">{match.summary}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-center gap-3"
          >
            <button
              onClick={onDecline}
              className="w-12 h-12 rounded-full border-2 border-white/30 bg-white/5 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:border-white/50 hover:bg-white/10 transition-all duration-300"
              aria-label="Pass"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>

            <button
              onClick={onAccept}
              className="px-8 py-3 rounded-full bg-white/90 hover:bg-white text-niyyah-purple font-medium text-sm transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(255,255,255,0.3)]"
              type="button"
            >
              Accept Introduction
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 2.2 }}
            className="mt-6 text-[10px] text-white/40 font-light"
          >
            This introduction is private to you
          </motion.p>
        </motion.div>
      )}

      <style>{`
        .gradient-background {
          background: linear-gradient(
            135deg,
            #7a6ba8 0%,
            #8b7db8 12%,
            #9b8fc4 25%,
            #8c9ed8 38%,
            #7ba5d9 50%,
            #6bc4e0 62%,
            #b89bc8 75%,
            #9b8fc4 88%,
            #7a6ba8 100%
          );
          background-size: 400% 400%;
          animation: gradientFluctuate 12s ease-in-out infinite;
        }

        @keyframes gradientFluctuate {
          0%, 100% { background-position: 0% 50%; }
          25% { background-position: 100% 50%; }
          50% { background-position: 50% 100%; }
          75% { background-position: 0% 100%; }
        }

        .grain-texture {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.15;
          mix-blend-mode: overlay;
          pointer-events: none;
        }
      `}</style>
    </motion.div>
  )
}